import { useEffect, useState } from "react";

import type { ClientId } from "../../domain/types";
import { useCoClient } from "../../data/store";
import { tallyBooksApi, type TallyCurrencyRow, type TallyCurrencyState } from "../../data/tallyBooksApi";
import { Callout } from "../../components/Callout";
import { SubStepShell } from "../../components/SubStepShell";
import { CoButton } from "../../components/Button";
import { useSubStepNav } from "../useSubStepNav";

const INPUT_CLASS = "h-9 w-full rounded-md border bg-background px-2 text-sm";

function displayName(row: TallyCurrencyRow): string {
  const name = row.name.trim();
  if (name !== "" && name.toUpperCase() !== row.code) return name;
  if (row.code === "INR") return "Indian Rupee";
  return "";
}

function displaySymbol(row: TallyCurrencyRow): string | null {
  const symbol = row.symbol.trim();
  if (symbol === "" || symbol === row.code) return null;
  return symbol;
}

export function TallyCurrencyStep({ clientId }: { clientId: ClientId }) {
  const client = useCoClient(clientId);
  const { goNext } = useSubStepNav(clientId, "currency");
  const [state, setState] = useState<TallyCurrencyState | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void tallyBooksApi.currencyState(clientId).then(
      (next) => {
        if (!cancelled) setState(next);
      },
      (reason: unknown) => {
        if (!cancelled) setError(reason instanceof Error ? reason.message : "Could not load currencies.");
      }
    );

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  useEffect(() => {
    if (!state?.pending.some((row) => row.status === "pending")) return undefined;
    const timer = window.setInterval(() => {
      void tallyBooksApi.currencyState(clientId).then(setState).catch(() => undefined);
    }, 2000);

    return () => window.clearInterval(timer);
  }, [clientId, state]);

  async function add() {
    setBusy(true);
    setError(null);
    try {
      setState(await tallyBooksApi.createCurrency(clientId, code));
      setCode("");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not create that currency.");
    } finally {
      setBusy(false);
    }
  }

  const picked = state?.available.find((item) => item.code === code);
  const clientName = client?.name || "this client";

  return (
    <SubStepShell
      title="Currency Settings"
      description={`Currencies in ${clientName}'s Tally company. INR is the base and is not created.`}
      thenWhat="Continue → Banks & Credit Cards."
      onContinue={() => void goNext()}
    >
      {error ? <Callout variant="warning">{error}</Callout> : null}
      {state === null ? (
        <p className="text-sm text-muted-foreground">Reading currencies…</p>
      ) : (
        <>
          <div className="flex flex-col gap-3">
            <div className="flex items-baseline justify-between gap-3">
              <h3 className="text-base font-medium">Enabled in Tally</h3>
              <span className="shrink-0 text-xs text-muted-foreground">
                {state.currencies.length} {state.currencies.length === 1 ? "currency" : "currencies"}
              </span>
            </div>
            <ul className="flex flex-col gap-1.5">
              {state.currencies.map((row) => {
                const symbol = displaySymbol(row);
                return (
                  <li key={row.code} className="flex items-center gap-3 rounded-md border border-border px-3 py-2 text-sm">
                    <span className="w-10 shrink-0 font-medium">{row.code}</span>
                    <span className="min-w-0 flex-1 truncate text-muted-foreground">{displayName(row)}</span>
                    {row.is_base ? (
                      <span className="shrink-0 rounded-sm bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
                        Base
                      </span>
                    ) : null}
                    <span className="min-w-4 shrink-0 text-right text-muted-foreground/80">{symbol ?? ""}</span>
                  </li>
                );
              })}
            </ul>
          </div>
          {state.pending.map((row) => (
            <p key={row.code} className={row.status === "failed" ? "text-sm text-destructive" : "text-sm text-muted-foreground"}>
              {row.status === "failed" ? row.error : `Creating ${row.code} in Tally…`}
            </p>
          ))}
          <div className="flex max-w-md flex-col gap-2">
            <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
              Add a currency
              <select className={INPUT_CLASS} value={code} disabled={busy} onChange={(event) => setCode(event.target.value)}>
                <option value="">Choose</option>
                {state.available.map((item) => (
                  <option key={item.code} value={item.code}>
                    {item.code} — {item.name}
                    {item.symbol ? ` (${item.symbol})` : ""}
                  </option>
                ))}
              </select>
            </label>
            {picked ? (
              <p className="text-xs text-muted-foreground">
                Symbol {picked.symbol || picked.code}. Formal name {picked.name}.
              </p>
            ) : null}
            <CoButton type="button" variant="outline" className="w-fit" disabled={busy || code === ""} onClick={() => void add()}>
              Create in Tally
            </CoButton>
          </div>
        </>
      )}
    </SubStepShell>
  );
}
